/**
 * OIDC Redirect Initiation
 *
 * Client-side helper to initiate the OIDC authentication flow.
 * Fetches CSRF token, builds auth URL, and redirects to Meta OIDC.
 */

import * as Sentry from '@sentry/nextjs';

export interface OidcRedirectOptions {
  /**
   * URL to redirect to after successful authentication.
   * Defaults to current page if not provided.
   */
  returnTo?: string;
  /**
   * Ask Meta OIDC to show its account picker instead of reusing the current account.
   */
  prompt?: 'select_account';
}

export interface OidcRedirectResult {
  success: boolean;
  error?: string;
}

/**
 * Initiate OIDC login flow by redirecting to the server-side start route.
 *
 * This function simply redirects to `/api/meta-oidc/start`, which handles:
 * 1. CSRF token generation
 * 2. Building the Meta OIDC authentication URL
 * 3. Server-side redirect to Meta OIDC
 *
 * @param options - Configuration options for the OIDC redirect
 * @returns Promise that resolves before redirect (only rejects on error)
 *
 * @example
 * ```tsx
 * import { initiateOidcLogin } from '@/lib/auth/oidcRedirect';
 *
 * async function handleLogin() {
 *   const result = await initiateOidcLogin();
 *
 *   if (!result.success) {
 *     console.error('Failed to initiate OIDC:', result.error);
 *   }
 *   // Browser will redirect if successful
 * }
 * ```
 */
export async function initiateOidcLogin(
  options: OidcRedirectOptions = {}
): Promise<OidcRedirectResult> {
  try {
    console.log('🔄 [OIDC] Redirecting to server start route...');

    // Redirect to server-side start route
    // The server handles CSRF token generation, state building, and Meta OIDC redirect
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams();
      if (options.prompt) {
        params.set('prompt', options.prompt);
      }
      const query = params.toString();
      window.location.href = `/api/meta-oidc/start${query ? `?${query}` : ''}`;
    }

    return { success: true };
  } catch (err) {
    const error = err instanceof Error ? err.message : 'OIDC login initiation failed';
    console.error('❌ [OIDC] Login initiation error:', err);
    Sentry.captureException(err, {
      tags: { service: 'oidcRedirect', operation: 'initiateOidcLogin' },
    });
    return { success: false, error };
  }
}
